import React, { useContext, useEffect, useState } from 'react';
import { LiveBtn } from '../liveBtn';
import { SmallMatchBoxAlt } from '../smallMatchBoxAlt';
import { ConnectionContext, displayLoader, displayErrorMessage, getEndpoint } from '../../utils';
import { getStoredData } from '../../utils/data-pulls';


/**
 * Match Info Widget:
 * Competition, Venue, and Attendance
 */
const LiveGamesWidget = (props) => {
    const { 
        ...restProps 
    } = props;

    const connectionInfo = useContext(ConnectionContext);

    // Error and load handling
    const [error, setError] = useState(null);
    const [isLoaded, setIsLoaded] = useState(false);

    const [ activeUIDs, setActiveUIDs ] = useState(null);
    const [ data, setData ] = useState(null);
    const [lastUpdatedTime, setLastUpdatedTime] = useState(null);
    const [ refreshCounter, setRefreshCounter ] = useState(0);

    /**
     * Get list of active UIDs
     */
    useEffect(() => {

        let args = [
            {
                key: 'activeUIDs'
            }
        ];
        getStoredData(args).then(
            (result) => {

                // Set Active UIDs
                if ( result.data.activeUIDs ) {
                    setActiveUIDs(result.data.activeUIDs);
                }

            }
        ).catch(
            (error) => {
                setError(error);
            }
        );

    }, []);


    /**
     * Get live game list, and refresh based on refreshCounter
     */
    useEffect(() => {
        if ( !connectionInfo || !activeUIDs ) return;

        // Get live match data
        const liveEndpoint = getEndpoint('match_list_live', {
            tenant: connectionInfo.tenant
        }, {
            organizationIdFilter: connectionInfo.organization_id
        }, connectionInfo.environment);
        
        fetch(liveEndpoint, {
            method: 'GET',
            headers: {
                'API_KEY': connectionInfo.api_key
            },
        })
          .then(res => res.json())
          .then(
            (result) => {
                if ( !result ) {
                    setData(null);
                    return;
                }
                
                const numberToShow = 2;
                if ( !result || result.length == 0 ) {
                  setData(null);
                } else {

                    /**
                     * API will return results for each province. 
                     * Use the list of active UIDs entered on the website to filter only to live matches we want to view
                     */
                    let validMatches = [];
                    result.forEach((match, i) => {
                        const exists = activeUIDs.includes(match.competition.id);
                        if ( exists ) {
                            validMatches.push(match);
                        }
                    });
                    
                    if ( validMatches.length == 0 ) {
                        setData(null);
                    } else if ( validMatches.length <= numberToShow ) {
                        setData(validMatches);
                    } else {
                        setData(validMatches.slice(0, numberToShow));
                    }

                }

                setIsLoaded(true); 

            },
            (error) => {
              setIsLoaded(true);
              setError(error);
              console.log(error);
            }
          );

    }, [connectionInfo, refreshCounter, activeUIDs]);


    /**
     * Setup countdown to refresh live matches
     */
    useEffect(() => {
        if ( !data ) return;
        const refreshRate = 60; // Every 60 seconds

        setTimeout(() => {
            setRefreshCounter(a => a + 1);
        }, refreshRate * 1000);
      
      }, [data, refreshCounter]);


    /**
     * Update 'last updated' ticker
     */
    useEffect(()=>{
        if ( !data ) return;
        let secondsSinceUpdate = 0;
        const refreshRate = 10; // Time in seconds

        setLastUpdatedTime('now');

        const interval = setInterval(() => {
            secondsSinceUpdate += 1;
            const secondsStr = secondsSinceUpdate == 1 ? ' second ago' : ' seconds ago';
            setLastUpdatedTime(secondsSinceUpdate + secondsStr);
        }, refreshRate * 100);

        return () => clearInterval(interval);
    }, [data, refreshCounter]);


     // Bail if we don't have data yet
     if (!isLoaded ) {
        return displayLoader();
    }

    // Display an error message if there's a problem
    if ( error ) {
        console.log(error);
        return displayErrorMessage();
    }


    return (
        <div className="acaWidget acaWidget--liveGames">

            <div className="acaWidget__header">
                <div className="acaWidget__headerMain">
                    <h4 className="acaH4 acaWidget__title">Live Games</h4>
                    { data ? (
                        <p className="acaWidget__description"><em>{`Scores last updated ${lastUpdatedTime}`}</em></p>
                    ) : '' }
                </div>
                <div className="acaWidget__headerAside">
                    { data ? (
                        <LiveBtn />
                    ) : '' }
                </div>
            </div>
            
            <div className="acaWidget__inner">
                { !data ? (
                    displayErrorMessage('There are no live events currently running.', 'acaError--simple')
                ) : (
                    <>
                        { data.map((row, i) => (
                            <SmallMatchBoxAlt key={i} data={row} />
                        )) }
                        { acaLiveURL ? (
                            <a href={acaLiveURL} className="acaBtn acaBtn--solid acaBtn--red acaBtn--soccerIcon">See All Live Games</a>
                        ) : '' }
                    </>
                ) }
            </div>

        </div>
    );
}





/**
 * Export
 */
export { LiveGamesWidget };

